import React, { useState, useMemo, ChangeEvent, createContext } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import RevealDropdown, { RevealDropdownTrigger } from 'components/base/RevealDropdown';
import ConnectorGroupTableActionDropdownItems from './ConnectorGroupTableActionDropdown';
import ConnectorGroupEditForm from './ConnectorGroupEditForm';
import SearchBox from 'components/common/SearchBox';
import useAdvanceTable from 'hooks/common/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { ConnectorGroup } from 'models/ConnectorGroup';
import { getConnectorGroupDeleteBlockMessage } from 'utils/connectorGroupDeletion';
import { Connector } from 'models/Connector';
import PhoenixDocCard from 'components/base/PhoenixDocCard';
import Button from 'components/base/Button';
import { Alert } from 'react-bootstrap';
import ConnectorGroupAddForm from './ConnectorGroupAddForm';
import WidgetsSectionTitle from 'components/base/WidgetsSectionTitle';

export type ConnectorGroupContextType = {
  handleEdit: (connectorGroup: ConnectorGroup) => void;
  handleDelete: (connectorGroup: ConnectorGroup) => void;
  getDeleteBlockMessage: (connectorGroup: ConnectorGroup) => string | null;
};

export const ConnectorGroupContext = createContext<ConnectorGroupContextType | null>(null);

type ConnectorGroupTableProps = {
  connectorGroups: ConnectorGroup[];
  connectors: Connector[];
  createConnectorGroup: (newConnectorGroup: ConnectorGroup) => Promise<void>;
  updateConnectorGroup: (id: number, updatedConnectorGroup: ConnectorGroup) => Promise<void>;
  deleteConnectorGroup: (id: number) => Promise<void>;
};

const ConnectorGroupManagementPanel: React.FC<ConnectorGroupTableProps> = ({
  connectorGroups,
  connectors,
  createConnectorGroup,
  updateConnectorGroup,
  deleteConnectorGroup,
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [notification, setNotification] = useState<string | null>(null);
  const [errorNotification, setErrorNotification] = useState<string | null>(null);
  const [editingConnectorGroup, setEditingConnectorGroup] = useState<ConnectorGroup | null>(null);
  const [formKey, setFormKey] = useState(0);
  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  const handleAddConnectorGroup = async (newConnectorGroup: ConnectorGroup) => {
    try {
      await createConnectorGroup(newConnectorGroup);
      setNotification(`One group added: ${newConnectorGroup.display_name}`);
      setTimeout(() => setNotification(null), 6000);
    } catch (error) {
      console.error("There was a problem with the add operation:", error);
    }
  };

  const handleEdit = (connectorGroup: ConnectorGroup) => {
    setEditingConnectorGroup(connectorGroup);
  };

  const handleSave = (updatedConnectorGroup: ConnectorGroup) => {
    updateConnectorGroup(updatedConnectorGroup.id, updatedConnectorGroup);
    setEditingConnectorGroup(null);
  };

  const handleCancel = () => {
    setEditingConnectorGroup(null);
  };

  const handleDelete = async (connectorGroup: ConnectorGroup) => {
    const blockMessage = getConnectorGroupDeleteBlockMessage(connectorGroup, connectors);
    if (blockMessage) {
      setErrorNotification(blockMessage);
      setTimeout(() => setErrorNotification(null), 6000);
      return;
    }

    const confirmDelete = window.confirm(`Are you sure you want to delete "${connectorGroup.display_name}"?`);
    if (!confirmDelete) {
      return;
    }

    try {
      await deleteConnectorGroup(connectorGroup.id);
      setNotification(`Group deleted: ${connectorGroup.display_name}`);
      setTimeout(() => setNotification(null), 6000);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete connector group';
      setErrorNotification(message);
      setTimeout(() => setErrorNotification(null), 6000);
    }
  };

  const handleRowClick = (id: number, name: string) => {
    const addressPageURL = `./group-connectors?id=${id}&name=${name}`;
    window.open(addressPageURL, "_self");
  };

  const columns: ColumnDef<ConnectorGroup>[] = useMemo(() => [
    {
      accessorKey: 'id',
      header: 'ID',
      meta: {
        headerProps: { className: 'py-3' },
        cellProps: { className: 'py-3' },
      },
      cell: ({ row }) => (
        <span
          onClick={() => handleRowClick(row.original.id, row.original.name)}
          style={{ cursor: 'pointer' }}
        >
          {row.original.id}
        </span>
      ),
    },
    {
      accessorKey: 'display_name',
      header: 'Name',
      meta: {
        headerProps: { className: 'py-3' },
        cellProps: { className: 'py-3' },
      },
      cell: ({ row }) => (
        <span
          onClick={() => handleRowClick(row.original.id, row.original.name)}
          style={{ cursor: 'pointer' }}
        >
          {row.original.display_name}
        </span>
      ),
    },
    {
      accessorKey: 'name',
      header: 'Directory Name',
      meta: {
        headerProps: { className: 'py-3' },
        cellProps: { className: 'py-3' },
      },
      cell: ({ row }) => (
        <span
          onClick={() => handleRowClick(row.original.id, row.original.name)}
          style={{ cursor: 'pointer' }}
        >
          {row.original.name}
        </span>
      ),
    },
    {
      id: 'action',
      cell: ({ row }) => (
        <RevealDropdownTrigger>
          <RevealDropdown>
            <ConnectorGroupTableActionDropdownItems connectorGroup={row.original} />
          </RevealDropdown>
        </RevealDropdownTrigger>
      ),
      meta: {
        headerProps: { style: { width: '7%' } },
        cellProps: { className: 'text-end' },
      },
    },
  ], []);

  const table = useAdvanceTable({
    data: connectorGroups,
    columns,
    pageSize: 10,
    pagination: true,
    sortable: true,
  });

  const contextValue = {
    handleEdit,
    handleDelete,
    getDeleteBlockMessage: (connectorGroup: ConnectorGroup) => getConnectorGroupDeleteBlockMessage(connectorGroup, connectors),
  };

  return (
    <ConnectorGroupContext.Provider value={contextValue}>
      <div>
        <PhoenixDocCard className="mb-4">
        {/* <WidgetsSectionTitle
        title="Groups"
        subtitle=""
        // icon={faPercentage}
        className="my-5"
      /> */}
            <PhoenixDocCard.Body>
            <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-3 mb-4">
              <Button
                className="addBtn"
                variant="primary"
                onClick={() => setShowAddForm(true)}
              >
                + Add Group
              </Button>
              <SearchBox
                placeholder="Search..."
                size="sm"
                onChange={handleSearchInputChange}
                className="ms-md-auto"
              />
            </div>

            {notification && (
              <Alert
                variant="outline-success"
                onClose={() => setNotification(null)}
                dismissible
              >
                {notification}
              </Alert>
            )}

            {errorNotification && (
              <Alert
                variant="outline-danger"
                onClose={() => setErrorNotification(null)}
                dismissible
              >
                {errorNotification}
              </Alert>
            )}

            <ConnectorGroupAddForm
              key={formKey}
              show={showAddForm}
              handleClose={() => {
                setShowAddForm(false);
                setFormKey(prevKey => prevKey + 1); 
              }}
              handleSave={handleAddConnectorGroup}
            />

            <div className="groups-table">
            <AdvanceTableProvider {...table}>
              <AdvanceTable
                tableProps={{
                  size: 'sm',
                  hover: true,
                  className: 'phoenix-table fs-9 mb-0 border-top border-200',
                }}
                rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
              />
              <AdvanceTableFooter navBtn />
            </AdvanceTableProvider>
            </div>
            {editingConnectorGroup && (
              <ConnectorGroupEditForm
                connectorGroup={editingConnectorGroup}
                onSave={handleSave}
                onCancel={handleCancel}
                handleUpdate={updateConnectorGroup}
              />
            )}

          </PhoenixDocCard.Body>
        </PhoenixDocCard>
      </div>
    </ConnectorGroupContext.Provider>
  );
};

export default ConnectorGroupManagementPanel;
